/**
 * Where a "Dig Deeper" vinyl match links to (TEM-1662).
 *
 * The links used to point at `https://record.tempest.app/vinyl/<id>`. Nothing
 * serves that domain, so every Dig Deeper click opened a dead page.
 *
 * A match's `id` is the Qdrant point's ORIGINAL id: the vector-search Lambda
 * (`ai-lambdas/vector-search/handler.py`) stores the string Record indexed under
 * as `payload.original_id` and hands it back as `id`. Record mints three shapes
 * (`stable_id` in `apps/record/src-tauri/src/ai/album_vector.rs`, and the
 * `album_{am_id}` fallback in `sync_enrichment_to_qdrant`, `ai/commands.rs`):
 *
 *   tempest_<project id>  a digitized capture. The web library opens a
 *                         `.tempest` project by that id at
 *                         `/library/album/<project id>`.
 *   album_<n>             an `album_metadata` row with no Discogs link. The
 *                         web library opens it as `apple-music-<n>`, the
 *                         spelling of `apple_music_album_id` on Record's server.
 *   discogs_<release id>  an album keyed by its Discogs release. No web-library
 *                         URL can name it: the library keys a record by its
 *                         project id or its `album_metadata` id, and a Discogs
 *                         release can sit under either. So this links to the
 *                         Discogs release page, which always resolves.
 *
 * Any other shape (an old point indexed before `original_id` existed answers
 * with a bare UUID) gets no link: `null`, and the caller renders the row
 * without one rather than a link to nowhere.
 */

/** The web library's origin + base. Spelled out; see `libraryUrl.ts` in `apps/record/web`. */
export const RECORD_LIBRARY_BASE = 'https://tempestrecord.com/library/';

export function digDeeperHref(matchId: string | null | undefined): string | null {
  if (!matchId) return null;

  const project = /^tempest_(.+)$/.exec(matchId);
  if (project) return `${RECORD_LIBRARY_BASE}album/${encodeURIComponent(project[1])}`;

  const albumMetadata = /^album_(\d+)$/.exec(matchId);
  if (albumMetadata) return `${RECORD_LIBRARY_BASE}album/apple-music-${albumMetadata[1]}`;

  const discogs = /^discogs_(\d+)$/.exec(matchId);
  if (discogs) return `https://www.discogs.com/release/${discogs[1]}`;

  return null;
}
